import React, { useState, useEffect } from 'react'
import { Settings, Cloud, FolderSync, Film, X, Check, AlertCircle, Upload, Trash2, Star, Play } from 'lucide-react'

interface StudioSettingsProps {
  isOpen: boolean
  onClose: () => void
}

interface S3Connection {
  connected: boolean
  bucket?: string
  region?: string
  buckets?: string[]
}

interface MonitoredFolder {
  id: string
  provider: string
  folder_path: string
  folder_name: string
  s3_bucket?: string
  s3_prefix?: string
  is_active: boolean
  auto_process: boolean
  files_processed: number
  last_checked_at?: string
}

interface Template {
  id: string
  template_type: 'intro' | 'outro' | 'watermark' | 'lower_third'
  name: string
  description?: string
  file_path: string
  duration_seconds?: number
  is_default: boolean
  transition_type: string
  transition_duration_ms: number
}

type SettingsTab = 's3' | 'monitoring' | 'templates'

export function StudioSettings({ isOpen, onClose }: StudioSettingsProps) {
  const [activeTab, setActiveTab] = useState<SettingsTab>('s3')
  const [availableProviders, setAvailableProviders] = useState<Set<string>>(new Set())
  
  // S3 State
  const [s3Connection, setS3Connection] = useState<S3Connection>({ connected: false })
  const [s3Form, setS3Form] = useState({
    access_key: '',
    secret_key: '',
    region: 'us-east-1',
    bucket: '',
    endpoint_url: ''
  })
  const [s3Loading, setS3Loading] = useState(false)
  const [s3Error, setS3Error] = useState('')
  const [s3Success, setS3Success] = useState('')
  
  // Monitored Folders State
  const [monitoredFolders, setMonitoredFolders] = useState<MonitoredFolder[]>([])
  const [foldersLoading, setFoldersLoading] = useState(false)
  
  // Templates State
  const [templates, setTemplates] = useState<Template[]>([])
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [uploadingTemplate, setUploadingTemplate] = useState(false)
  const [newTemplate, setNewTemplate] = useState({
    template_type: 'intro' as Template['template_type'],
    name: '',
    file_path: '',
    transition_type: 'fade',
    transition_duration_ms: 500
  })

  const regions = [
    'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
    'eu-west-1', 'eu-west-2', 'eu-central-1',
    'ap-northeast-1', 'ap-southeast-1', 'ap-southeast-2'
  ]

  // Check S3 connection on mount
  useEffect(() => {
    if (isOpen) {
      ;(async () => {
        try {
          const response = await fetch('/api/storage/providers')
          if (response.ok) {
            const data = await response.json()
            const keys = Array.isArray(data.providers) ? data.providers.map((p: any) => p?.name).filter(Boolean) : []
            setAvailableProviders(new Set(keys))
          }
        } catch (error) {
          console.error('Failed to load storage providers:', error)
        }
      })()

      checkS3Connection()
      loadMonitoredFolders()
      loadTemplates()
    }
  }, [isOpen])

  const checkS3Connection = async () => {
    try {
      const response = await fetch('/api/storage/connections')
      if (response.ok) {
        const data = await response.json()
        const s3Conn = data.connections?.find((c: any) => c.provider === 's3')
        if (s3Conn) {
          setS3Connection({ connected: true, bucket: s3Conn.email })
        }
      }
    } catch (error) {
      console.error('Failed to check S3 connection:', error)
    }
  }

  const connectS3 = async () => {
    if (!availableProviders.has('s3')) {
      setS3Error('S3 provider is not available on this server (not configured).')
      return
    }

    setS3Loading(true)
    setS3Error('')
    setS3Success('')
    
    try {
      const response = await fetch('/api/storage/s3/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(s3Form)
      })
      
      const data = await response.json()
      
      if (response.ok) {
        setS3Success('S3 connected successfully!')
        setS3Connection({
          connected: true,
          bucket: s3Form.bucket,
          region: s3Form.region,
          buckets: data.buckets
        })
        // Clear sensitive fields
        setS3Form(prev => ({ ...prev, secret_key: '' }))
      } else {
        setS3Error(data.error || 'Failed to connect')
      }
    } catch (error) {
      setS3Error('Failed to connect to S3')
    } finally {
      setS3Loading(false)
    }
  }

  const loadMonitoredFolders = async () => {
    setFoldersLoading(true)
    try {
      const response = await fetch('/api/storage/monitored-folders')
      if (response.ok) {
        const data = await response.json()
        setMonitoredFolders(data.folders || [])
      }
    } catch (error) {
      console.error('Failed to load monitored folders:', error)
    } finally {
      setFoldersLoading(false)
    }
  }

  const toggleMonitoredFolder = async (folderId: string) => {
    try {
      const response = await fetch(`/api/storage/monitored-folders/${folderId}/toggle`, {
        method: 'POST'
      })
      if (response.ok) {
        loadMonitoredFolders()
      }
    } catch (error) {
      console.error('Failed to toggle folder:', error)
    }
  }

  const removeMonitoredFolder = async (folderId: string) => {
    if (!confirm('Remove this folder from monitoring?')) return
    
    try {
      const response = await fetch(`/api/storage/monitored-folders/${folderId}`, {
        method: 'DELETE'
      })
      if (response.ok) {
        loadMonitoredFolders()
      }
    } catch (error) {
      console.error('Failed to remove folder:', error)
    }
  }

  const loadTemplates = async () => {
    setTemplatesLoading(true)
    try {
      const response = await fetch('/api/storage/templates')
      if (response.ok) {
        const data = await response.json()
        setTemplates(data.templates || [])
      }
    } catch (error) {
      console.error('Failed to load templates:', error)
    } finally {
      setTemplatesLoading(false)
    }
  }

  const createTemplate = async () => {
    if (!newTemplate.name || !newTemplate.file_path) {
      alert('Name and file path are required')
      return
    }
    
    setUploadingTemplate(true)
    try {
      const response = await fetch('/api/storage/templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTemplate)
      })
      
      if (response.ok) {
        loadTemplates()
        setNewTemplate({
          template_type: 'intro',
          name: '',
          file_path: '',
          transition_type: 'fade',
          transition_duration_ms: 500
        })
      }
    } catch (error) {
      console.error('Failed to create template:', error)
    } finally {
      setUploadingTemplate(false)
    }
  }

  const setDefaultTemplate = async (templateId: string) => {
    try {
      await fetch(`/api/storage/templates/${templateId}/set-default`, { method: 'POST' })
      loadTemplates()
    } catch (error) {
      console.error('Failed to set default:', error)
    }
  }

  const deleteTemplate = async (templateId: string) => {
    if (!confirm('Delete this template?')) return
    
    try {
      await fetch(`/api/storage/templates/${templateId}`, { method: 'DELETE' })
      loadTemplates()
    } catch (error) {
      console.error('Failed to delete template:', error)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-xl border border-gray-600 w-full max-w-3xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-600">
          <div className="flex items-center gap-2">
            <Settings size={20} className="text-blue-400" />
            <h2 className="text-lg font-semibold text-white">Studio Settings</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-700 rounded">
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-600">
          <button
            onClick={() => setActiveTab('s3')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 's3' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-white'
            }`}
          >
            <Cloud size={16} />
            S3 Storage
          </button>
          <button
            onClick={() => setActiveTab('monitoring')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'monitoring' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-white'
            }`}
          >
            <FolderSync size={16} />
            Folder Monitoring
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'templates' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-white'
            }`}
          >
            <Film size={16} />
            Intro/Outro
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* S3 Tab */}
          {activeTab === 's3' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-white font-medium mb-2">Amazon S3 / S3-Compatible Storage</h3>
                <p className="text-gray-400 text-sm">
                  Connect your own S3 bucket or S3-compatible storage (MinIO, Wasabi, etc.)
                </p>
              </div>

              {s3Connection.connected ? (
                <div className="p-4 bg-green-900/30 border border-green-700 rounded-lg">
                  <div className="flex items-center gap-2 text-green-400">
                    <Check size={18} />
                    <span>S3 Connected</span>
                  </div>
                  <p className="text-gray-400 text-sm mt-1">
                    Bucket: {s3Connection.bucket || 'Default'}
                  </p>
                </div>
              ) : null}

              {s3Error && (
                <div className="p-4 bg-red-900/30 border border-red-700 rounded-lg">
                  <div className="flex items-center gap-2 text-red-400">
                    <AlertCircle size={18} />
                    <span>{s3Error}</span>
                  </div>
                </div>
              )}

              {s3Success && (
                <div className="p-4 bg-green-900/30 border border-green-700 rounded-lg">
                  <div className="flex items-center gap-2 text-green-400">
                    <Check size={18} />
                    <span>{s3Success}</span>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Access Key ID</label>
                  <input
                    type="text"
                    value={s3Form.access_key}
                    onChange={(e) => setS3Form({ ...s3Form, access_key: e.target.value })}
                    placeholder="AKIA..."
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Secret Access Key</label>
                  <input
                    type="password"
                    value={s3Form.secret_key}
                    onChange={(e) => setS3Form({ ...s3Form, secret_key: e.target.value })}
                    placeholder="••••••••"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Region</label>
                  <select
                    value={s3Form.region}
                    onChange={(e) => setS3Form({ ...s3Form, region: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  >
                    {regions.map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Default Bucket</label>
                  <input
                    type="text"
                    value={s3Form.bucket}
                    onChange={(e) => setS3Form({ ...s3Form, bucket: e.target.value })}
                    placeholder="my-video-bucket"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm text-gray-300 mb-1">
                    Custom Endpoint URL <span className="text-gray-500">(optional, for S3-compatible services)</span>
                  </label>
                  <input
                    type="text"
                    value={s3Form.endpoint_url}
                    onChange={(e) => setS3Form({ ...s3Form, endpoint_url: e.target.value })}
                    placeholder="https://s3.wasabisys.com"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              <button
                onClick={connectS3}
                disabled={s3Loading || !s3Form.access_key || !s3Form.secret_key}
                className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                {s3Loading ? 'Connecting...' : s3Connection.connected ? 'Update Connection' : 'Connect S3'}
              </button>
            </div>
          )}

          {/* Monitoring Tab */}
          {activeTab === 'monitoring' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-white font-medium mb-2">Folder Monitoring</h3>
                <p className="text-gray-400 text-sm">
                  Monitor folders in your cloud storage. New videos will be automatically queued for processing.
                </p>
              </div>

              {foldersLoading ? (
                <div className="text-center py-8 text-gray-400">Loading...</div>
              ) : monitoredFolders.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-gray-600 rounded-lg">
                  <FolderSync size={40} className="mx-auto text-gray-500 mb-3" />
                  <p className="text-gray-400">No folders being monitored</p>
                  <p className="text-gray-500 text-sm mt-1">
                    Go to Cloud Storage tab to select folders to monitor
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {monitoredFolders.map((folder) => (
                    <div
                      key={folder.id}
                      className={`p-4 rounded-lg border ${
                        folder.is_active ? 'bg-gray-700 border-gray-600' : 'bg-gray-800 border-gray-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${folder.is_active ? 'bg-green-500' : 'bg-gray-500'}`} />
                          <div>
                            <p className="text-white font-medium">{folder.folder_name || folder.folder_path}</p>
                            <p className="text-gray-400 text-sm">
                              {folder.provider.toUpperCase()} • {folder.files_processed} files processed
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleMonitoredFolder(folder.id)}
                            className={`px-3 py-1 text-sm rounded ${
                              folder.is_active
                                ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                                : 'bg-green-600 hover:bg-green-700 text-white'
                            }`}
                          >
                            {folder.is_active ? 'Pause' : 'Resume'}
                          </button>
                          <button
                            onClick={() => removeMonitoredFolder(folder.id)}
                            className="p-1 text-red-400 hover:bg-red-900/30 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Templates Tab */}
          {activeTab === 'templates' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-white font-medium mb-2">Intro & Outro Templates</h3>
                <p className="text-gray-400 text-sm">
                  Add intro and outro videos to automatically apply to your exported clips.
                </p>
              </div>

              {/* Add New Template */}
              <div className="p-4 bg-gray-700/50 rounded-lg border border-gray-600">
                <h4 className="text-white font-medium mb-3">Add New Template</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Type</label>
                    <select
                      value={newTemplate.template_type}
                      onChange={(e) => setNewTemplate({ ...newTemplate, template_type: e.target.value as Template['template_type'] })}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                    >
                      <option value="intro">Intro</option>
                      <option value="outro">Outro</option>
                      <option value="watermark">Watermark</option>
                      <option value="lower_third">Lower Third</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Name</label>
                    <input
                      type="text"
                      value={newTemplate.name}
                      onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                      placeholder="My Intro"
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">File Path / URL</label>
                    <input
                      type="text"
                      value={newTemplate.file_path}
                      onChange={(e) => setNewTemplate({ ...newTemplate, file_path: e.target.value })}
                      placeholder="/path/to/intro.mp4 or S3 key"
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Transition</label>
                    <div className="flex gap-2">
                      <select
                        value={newTemplate.transition_type}
                        onChange={(e) => setNewTemplate({ ...newTemplate, transition_type: e.target.value })}
                        className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                      >
                        <option value="cut">Cut</option>
                        <option value="fade">Fade</option>
                        <option value="dissolve">Dissolve</option>
                        <option value="wipe">Wipe</option>
                      </select>
                      <input
                        type="number"
                        value={newTemplate.transition_duration_ms}
                        onChange={(e) => setNewTemplate({ ...newTemplate, transition_duration_ms: parseInt(e.target.value) })}
                        placeholder="500"
                        className="w-20 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                      />
                      <span className="text-gray-400 self-center">ms</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={createTemplate}
                  disabled={uploadingTemplate || !newTemplate.name || !newTemplate.file_path}
                  className="mt-4 flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg"
                >
                  <Upload size={16} />
                  {uploadingTemplate ? 'Adding...' : 'Add Template'}
                </button>
              </div>

              {/* Existing Templates */}
              {templatesLoading ? (
                <div className="text-center py-8 text-gray-400">Loading...</div>
              ) : templates.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-gray-600 rounded-lg">
                  <Film size={40} className="mx-auto text-gray-500 mb-3" />
                  <p className="text-gray-400">No templates yet</p>
                  <p className="text-gray-500 text-sm mt-1">
                    Add an intro or outro template above
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {['intro', 'outro', 'watermark', 'lower_third'].map((type) => {
                    const typeTemplates = templates.filter(t => t.template_type === type)
                    if (typeTemplates.length === 0) return null
                    
                    return (
                      <div key={type} className="space-y-2">
                        <h4 className="text-gray-300 text-sm font-medium capitalize">{type.replace('_', ' ')}s</h4>
                        {typeTemplates.map((template) => (
                          <div
                            key={template.id}
                            className={`p-3 rounded-lg border flex items-center justify-between ${
                              template.is_default
                                ? 'bg-blue-900/30 border-blue-700'
                                : 'bg-gray-700 border-gray-600'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              {template.is_default && <Star size={16} className="text-yellow-400" />}
                              <div>
                                <p className="text-white">{template.name}</p>
                                <p className="text-gray-400 text-sm">
                                  {template.transition_type} • {template.transition_duration_ms}ms
                                  {template.duration_seconds && ` • ${template.duration_seconds.toFixed(1)}s`}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setDefaultTemplate(template.id)}
                                disabled={template.is_default}
                                className="px-2 py-1 text-xs bg-gray-600 hover:bg-gray-500 disabled:opacity-50 text-white rounded"
                              >
                                {template.is_default ? 'Default' : 'Set Default'}
                              </button>
                              <button
                                onClick={() => deleteTemplate(template.id)}
                                className="p-1 text-red-400 hover:bg-red-900/30 rounded"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
